import classNames from 'classnames';
import { Dropdown, type MenuProps, Tooltip } from 'antd';
import { forwardRef, memo, type CSSProperties, type ForwardedRef, type ReactNode } from 'react';
import type { DraggableAttributes } from '@dnd-kit/core';
import type { QueueListEntry } from '../../../../../lib/playback/queueDisplay';
import { formatQueueDuration } from '../../../../../lib/playback/queueDisplay';
import { DeleteIcon, GripHandleIcon, MenuDots } from '../../../../Icons';

type DragHandleProps = {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  attributes?: DraggableAttributes;
  listeners?: Record<string, any>;
  disabled?: boolean;
  hidden?: boolean;
};

interface QueueListItemProps {
  entry: QueueListEntry;
  disabled?: boolean;
  onSelect?: (index: number) => void;
  indent?: boolean;
  onRemove?: (entry: QueueListEntry) => void;
  removeDisabled?: boolean;
  removePending?: boolean;
  className?: string;
  style?: CSSProperties;
  dragHandleProps?: DragHandleProps;
  isDragging?: boolean;
  actionsNode?: ReactNode;
}

const QueueListItemComponent = (
  {
    entry,
    disabled,
    onSelect,
    indent,
    onRemove,
    removeDisabled,
    removePending,
    className,
    style,
    dragHandleProps,
    isDragging,
    actionsNode
  }: QueueListItemProps,
  ref: ForwardedRef<HTMLLIElement>
) => {
    const handleSelect = () => {
      if (disabled || !onSelect) return;
      onSelect(entry.queueIndex);
    };

  const showActionsButton = Boolean(onRemove);
  const menuItems: MenuProps['items'] = [
    {
      key: 'queue-remove',
      label: removePending ? 'Removing…' : 'Remove from queue',
      icon: <DeleteIcon />,
      danger: true,
      disabled: Boolean(removeDisabled || disabled || removePending),
      onClick: () => {
        if (!onRemove || removeDisabled || disabled || removePending) return;
        onRemove(entry);
      }
    }
  ];

  return (
    <li
      ref={ref}
      className={classNames(
        'queue-list__item',
        {
          'queue-list__item--current': entry.isCurrent,
          'queue-list__item--past': entry.isPast,
          'queue-list__item--recording': entry.isSameRecording,
          'queue-list__item--disabled': disabled,
          'queue-list__item--indented': indent,
          'queue-list__item--dragging': isDragging
        },
        className
      )}
      style={style}
      data-testid={entry.isCurrent ? 'queue-current' : 'queue-item'}
      data-queue-index={entry.queueIndex}
      data-queue-position={entry.position}
    >
      <div className='queue-list__item-inner'>
        <button
          type='button'
          className='queue-list__item-button'
          onClick={handleSelect}
          disabled={disabled || entry.isCurrent}
        >
          <span className='queue-list__details'>
            <span className='queue-list__title' title={entry.metadata.title ?? undefined}>
              {entry.metadata.displayTitle ?? entry.metadata.title ?? `Track ${entry.position}`}
            </span>
            {entry.metadata.artists ? (
              <span className='queue-list__artists' title={entry.metadata.artists}>
                {entry.metadata.artists}
              </span>
            ) : null}
          </span>
          <span className='queue-list__duration'>{formatQueueDuration(entry.metadata.durationMs)}</span>
        </button>
        {actionsNode
          ? actionsNode
          : showActionsButton ? (
              <Tooltip title='More options'>
                <Dropdown
                  trigger={['click']}
                  placement='bottomRight'
                  destroyPopupOnHide
                  menu={{ items: menuItems }}
                >
                  <button
                    type='button'
                    className={classNames('queue-list__more-button', {
                      'queue-list__more-button--pending': removePending,
                    })}
                    aria-label={`More options for ${entry.metadata.title ?? 'track'}`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    disabled={Boolean(disabled || removeDisabled)}
                  >
                    <MenuDots />
                  </button>
                </Dropdown>
              </Tooltip>
            ) : null}
        {dragHandleProps && !dragHandleProps.hidden ? (
          <button
            type='button'
            className={classNames('queue-list__drag-handle', {
              'queue-list__drag-handle--disabled': dragHandleProps.disabled
            })}
            ref={dragHandleProps.setActivatorNodeRef}
            {...dragHandleProps.attributes}
            {...dragHandleProps.listeners}
            aria-label='Drag to reorder'
            disabled={Boolean(dragHandleProps.disabled)}
            tabIndex={dragHandleProps.disabled ? -1 : 0}
          >
            <GripHandleIcon />
          </button>
        ) : null}
      </div>
    </li>
  );
};

const QueueListItem = memo(forwardRef<HTMLLIElement, QueueListItemProps>(QueueListItemComponent));

QueueListItem.displayName = 'QueueListItem';

export default QueueListItem;
